import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import * as mammoth from 'mammoth';
import html2pdf from 'html2pdf.js';

const typeMap: { [key: string]: string } = {
  'application/vnd.ms-excel': 'sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'sheet',
  'application/x-cfb': 'sheet',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'image/png': 'png',
  'image/jpeg': 'jpeg'
};

export async function detectFileType(
  file: File
): Promise<{ mime: string; ext: string } | null> {
  const { fileTypeFromBuffer } = await import('file-type'); // dynamic import workaround
  if (!file) return null;
  console.log('Getting File type ===================================');
  const buffer = await file.slice(0, 4100).arrayBuffer();
  const type = await fileTypeFromBuffer(new Uint8Array(buffer));
  console.log('type', type);
  console.log('type ext', type?.ext);
  const mime = typeMap[`${type?.mime}`];
  console.log('mime', mime);
  if (!mime) {
    return null;
  } else {
    return { mime: mime, ext: type?.ext! };
  }
}

/**
 * DOCX -> PDF (in-memory) -> page count, with correct pagination.
 */
export const getDOCXPageCount = async (file: File): Promise<number> => {
  GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

  // 96dpi CSS pixel approximations for A4
  const A4_WIDTH_PX = 794; // 210mm @ ~96dpi
  const A4_HEIGHT_PX = 2664; // 297mm @ ~96dpi

  // 1) DOCX -> HTML
  const ab = await file.arrayBuffer();
  const { value: html, messages } = await mammoth.convertToHtml(
    {
      arrayBuffer: ab
    },
    { ignoreEmptyParagraphs: false }
  );

  console.log('messages', messages);

  // 2) Render HTML off-screen with a fixed page width in *pixels*
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = `${A4_WIDTH_PX}px`;
  host.style.maxWidth = `${A4_WIDTH_PX}px`;
  host.style.boxSizing = 'border-box';
  host.style.whiteSpace = 'normal'; // prevent accidental no-wrap
  host.innerHTML = html;
  document.body.appendChild(host);
  console.log('scroll height', host.scrollHeight);
  const pagesEstimate = Math.ceil(host.scrollHeight / A4_HEIGHT_PX);
  console.log('pages estimate', pagesEstimate);
  try {
    // 3) HTML -> PDF blob (paginate instead of squeezing to one page)
    const pdfBlob: Blob = await html2pdf()
      .from(host)
      .set({
        // let html2pdf paginate; margins are in jsPDF units (set below)
        margin: [10, 10, 10, 10],
        // pagebreak: {
        //   // honor CSS page-breaks if present and legacy behavior
        //   mode: ['css', 'legacy']
        //   // You can force breaks inserting <div class="html2pdf__page-break"></div>
        // },
        html2canvas: {
          // Render at higher pixel density for clarity
          scale: 1,
          useCORS: true,
          // Tell html2canvas what "window" size to render at (prevents auto-shrink)
          windowWidth: A4_WIDTH_PX,
          windowHeight: host.scrollHeight // allow full content height
        },
        // Use 'mm' + 'a4' so jsPDF knows the true page size
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .outputPdf('blob');

    // 4) Count pages via pdf.js (your pattern)
    const pdfAB = await pdfBlob.arrayBuffer();
    const loadingTask = getDocument({ data: pdfAB });
    const pdf = await loadingTask.promise;
    return pdf.numPages;
  } finally {
    document.body.removeChild(host);
  }
};
// GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
export const getPDFPageCount = async (file: File) => {
  GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;
  const ab = await file.arrayBuffer();
  const loadingTask = getDocument({ data: ab });
  const pdf = await loadingTask.promise;
  return pdf.numPages;
};

// export const getPageCount = async (file: File): Promise<number> => {
//   GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

//   const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
//   if (isPdf) {
//     const ab = await file.arrayBuffer();
//     const loadingTask = getDocument({ data: ab });
//     const pdf = await loadingTask.promise;
//     return pdf.numPages;
//   }

//   // Fallback to DOCX flow
//   return getDOCXPageCount(file);
// };
